import ColourService from '@/shared/services/color';
import { SeriesConfig } from '@/features/reports/models';
import type { ChartOptions, TooltipItem } from 'chart.js';
import { CrosshairOptions } from 'chartjs-plugin-crosshair';

// Extend Chart.js types to include 'crosshair' plugin options
declare module 'chart.js' {
  interface PluginOptionsByType<TType> {
    crosshair?: CrosshairOptions | false;
  }
}

export class ChartConfig {
  public static get gridChartsGlobalOptions(): ChartOptions<'bar'> {
    const options = ChartConfig.globalOptions;
    options.scales = {
      x: {
        ticks: {
          autoSkip: true,
          maxTicksLimit: 10,
        },
        grid: {
          display: false,
        },
      },
    };
    options.plugins = {
      ...options.plugins,
      crosshair: {
        sync: {
          enabled: false,
        },
        line: {
          color: 'red',
          width: 1,
        },
        zoom: {
          enabled: false,
        },
        snap: {
          enabled: true,
        },
      },
    };
    return options;
  }

  public static get globalOptions(): ChartOptions<'bar'> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          // Uncomment to disable legend click event
          // onClick: () => { /**/ }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (tooltipItem: TooltipItem<'bar'>) => {
              const value = tooltipItem.formattedValue; // Chart.js 4 uses formattedValue
              return ChartConfig.formatNumber(value);
            },
          },
        },
      },
      animation: {
        duration: 1000, // General animation time
      },
      hover: {
        mode: 'index',
        intersect: false,
      },
    };
  }

  public static backgroundPalette(orderedSeriesConfig: SeriesConfig[]): string[] {
    const palette = orderedSeriesConfig.map((config) => {
      return config.colour || this.metricPalette[config.name] || this.metricPalette.Default;
    });
    return this.repeat(
      orderedSeriesConfig.length,
      palette.map((color) => {
        if (color.startsWith('#')) {
          return ColourService.setAlpha(color, 0.2);
        }
        return color; // Return as-is if not a hex color
      }),
    );
  }

  public static borderPalette(orderedSeriesConfig: SeriesConfig[]): string[] {
    const palette = orderedSeriesConfig.map((config) => {
      return config.colour || this.metricPalette[config.name] || this.metricPalette.Default; // Ensure fallback
    });
    return this.repeat(
      orderedSeriesConfig.length,
      palette.map((color) => {
        return ColourService.darken(color, 1);
      }),
    );
  }

  public static backgroundColoursForRandomItems(length: number): string[] {
    return [...this.unknownItemPalette].slice(0, length); // Use slice instead of splice for non-destructive copy
  }

  public static singleColourPalette(length: number, colour: string): string[] {
    return Array(length).fill(colour);
  }

  private static metricPalette: { [key: string]: string } = {
    Default: '#FF6666',
    Started: '#E6C300',
    Engaged: '#FF6666',
    Completed: '#062B80',
    HandedOver: '#FF6666',
    Leads: '#062B80',
    Users: '#FF6666',
    Conversations: '#062B80',
  };

  private static unknownItemPalette: string[] = [
    '#4A3B79',
    '#774674',
    '#BB566D',
    '#FF6666',
    '#F87F4A',
    '#EDAA1C',
    '#E6C300',
    '#A99A23',
    '#576251',
    '#43545D',
  ];

  private static repeat<T>(length: number, array: T[]): T[] {
    return Array(Math.ceil(length / array.length))
      .fill(array)
      .flat()
      .slice(0, length); // Fixed repeat logic
  }

  private static formatNumber(value: string): string {
    const n = Number(value);
    if (isNaN(n)) {
      return value;
    }
    return n.toLocaleString();
  }
}
